import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { DISPLAY_MONTHS_OPTIONS } from '@/customerForecastModule/constants';
import { IForecastAccuracyData } from '@/customerForecastModule/domains/entities/forecastAccuracyRateResult';
import { diContainer } from '@/coreContainer/container';
import { useTablePreferences } from '@/corePagesModule/composables/useTablePreferences';

const t = i18n.t;

// テーブル識別キー（ソート設定の永続化用）
const FORECAST_ACCURACY_TABLE_TYPE = 'forecastAccuracyTable';

export const useForecastAccuracyStore = defineStore('forecastAccuracy', () => {
  // Table data (API already handles pagination, filtering, and sorting)
  const tableData = ref<IForecastAccuracyData[]>([]);

  // Table preferences composable
  const { saveSort } = useTablePreferences();

  // UI state
  const state = reactive({
    receivedForecastYearMonthInfo: { year: new Date().getFullYear(), month: new Date().getMonth() + 1 } as IYearMonth,
    displayForecastsMonth: 6,
    tableContents: {
      renderingKey: 0,
      tableHeaders: {} as TableContentHeader,
      loadingState: {
        status: {
          type: 'loading',
        },
      } as LoadingState,
      pagingOptions: initPagingOptions(30) as IPagingOptions,
      // Sort state
      sortKey: undefined as string | undefined,
      sortTargetYearMonth: undefined as IYearMonth | undefined,
      isAsc: true as boolean,
    },
    openedSelectInputType: undefined as string | undefined,
    selectedResponsibleUserIdList: [] as string[],
    // Download modal state
    downloadModalState: {
      isOpen: false,
      targetForecastForReceiptId: '' as string,
      targetForecastName: '' as string,
      startYearMonth: { year: 0, month: 0 } as IYearMonth,
      endYearMonth: { year: 0, month: 0 } as IYearMonth,
    },
  });

  /**
   * Fetch forecast accuracy data from API
   * APIから内示正答率データを取得
   */
  const fetchForecastAccuracyData = async () => {
    state.tableContents.loadingState = {
      status: {
        type: 'loading',
      },
    } as LoadingState;

    try {
      // Get responsible user ID if selected (use first one if multiple selected)
      const responsibleUserId = state.selectedResponsibleUserIdList.length > 0 ? state.selectedResponsibleUserIdList[0] : undefined;

      // バックエンドに渡すソートキーを変換
      const backendSortKey = convertUiKeyToBackendKey(state.tableContents.sortKey);

      // Call UseCase to get forecast accuracy data
      const result = await diContainer.getForecastAccuracyRateUseCase.Execute(
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        state.displayForecastsMonth,
        state.tableContents.pagingOptions.pageSize,
        state.tableContents.pagingOptions.offset,
        responsibleUserId,
        backendSortKey,
        state.tableContents.isAsc,
        state.tableContents.sortTargetYearMonth?.year,
        state.tableContents.sortTargetYearMonth?.month
      );

      if (result.status.type === 'error') {
        throw result.status.error;
      }

      // Get adapted data from UseCase
      const adaptedData = result.status.data;

      // Store table data (API already handles pagination)
      tableData.value = adaptedData.data;

      // Update pagination from API response
      state.tableContents.pagingOptions = {
        ...state.tableContents.pagingOptions,
        offset: adaptedData.pagingOptions.offset,
        totalResultsAvailable: adaptedData.pagingOptions.totalResultsAvailable,
        returnCount: adaptedData.pagingOptions.returnCount,
      };

      // Update loading state based on results
      if (adaptedData.data.length === 0) {
        state.tableContents.loadingState = {
          status: {
            type: 'zeroMatch',
          },
        } as LoadingState;
      } else {
        state.tableContents.loadingState = {
          status: {
            type: 'success',
          },
        } as LoadingState;
      }
    } catch (error) {
      console.error('Error fetching forecast accuracy data:', error);
      state.tableContents.loadingState = {
        status: {
          type: 'error',
          errorMessage: t('データ取得に失敗しました'),
        },
      } as LoadingState;
    }
  };

  /**
   * Get table headers
   * テーブルヘッダー情報を取得する
   * @param forecastCount - 表示する内示の数
   * @returns ヘッダー情報
   */
  const getTableHeaders = (forecastCount: number): TableContentHeader => {
    const forecasts: TableContentProperty[] = [];
    for (let i = 0; i < forecastCount; i++) {
      forecasts.push({
        key: `customerAnnounceForAccuracy-${i + 1}`,
        isVariableWidth: true,
        isNarrowable: true,
        isEditable: false,
        sortOption: {
          isSortable: true,
          isAsc: true,
        },
        propertyPath: { paths: [], propertyType: 'number' },
      });
    }

    return {
      contentProperties: [
        {
          key: 'ForecastAccuracyName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptName'], propertyType: 'string' },
        },
        {
          key: 'ForecastAccuracyResponsibleUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptResponsibleUser'], propertyType: 'string' },
        },
        {
          key: 'ForecastAccuracyPreviousMonthSalesResult',
          isVariableWidth: false,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          headerWidth: 180,
          propertyPath: { paths: ['previousMonthSalesResult'], propertyType: 'number' },
        },
        {
          key: 'ForecastAccuracyPreviousMonthForecast',
          isVariableWidth: false,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          headerWidth: 180,
          propertyPath: { paths: ['previousMonthForecast'], propertyType: 'number' },
        },
        {
          key: 'ForecastAccuracyPreviousMonthAccuracyRate',
          isVariableWidth: false,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          headerWidth: 120,
          propertyPath: { paths: ['previousMonthAccuracyRate'], propertyType: 'number' },
        },
        {
          key: 'Space8px',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          headerWidth: 8,
          propertyPath: { paths: [], propertyType: 'string' },
        },
        ...forecasts,
        {
          key: 'ForecastAccuracyMenu',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          headerWidth: 40,
          propertyPath: { paths: [], propertyType: 'string' },
        },
      ],
      options: {
        stableColumnCount: 0,
        resizableHeaderColumn: false,
      },

      forecastStartYearMonth: DateService.getShiftedIYearMonthByMonths(
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        -(state.displayForecastsMonth + 1)
      ),
    };
  };

  // Computed properties
  /**
   * Get table content items
   * テーブルコンテンツアイテムを取得（APIが既にページネーションを処理済み）
   */
  const tableContentItems = computed((): TableContentItem[] => {
    if (!tableData.value || tableData.value.length === 0) {
      return [];
    }

    // API already handles pagination, so use data directly
    return tableData.value.map((item) => ({
      displayId: item.forecastForReceiptId,
      isVirtualChild: false,
      forecastForReceiptName: item.forecastForReceiptName,
      forecastForReceiptResponsibleUsers: item.forecastForReceiptResponsibleUsers || [],
      previousMonthSalesResult: item.previousMonthSalesResult,
      previousMonthForecast: item.previousMonthForecast,
      previousMonthAccuracyRate: item.previousMonthAccuracyRate,
      customerForecasts: item.customerForecasts || [],
    })) as TableContentItem[];
  });

  /**
   * Months options for display forecast
   * 表示月数の選択肢 (3-24 months)
   */
  const monthsOptionsDisplayForecast = computed(() => {
    return DISPLAY_MONTHS_OPTIONS.map((month) => month + t('unitMonth'));
  });

  /**
   * Table headers computed
   * テーブルヘッダーを計算（表示月数に応じて動的に変更、現在のソート状態を反映）
   */
  const tableHeaders = computed(() => {
    const headers = getTableHeaders(state.displayForecastsMonth);
    // 現在のソート状態をヘッダーに反映
    if (state.tableContents.sortKey) {
      headers.contentProperties = headers.contentProperties.map((property) => {
        const currentIsAsc = state.tableContents.isAsc ?? true;
        property.sortOption.isAsc = property.key === state.tableContents.sortKey ? currentIsAsc : true;
        return property;
      });
    }
    return headers;
  });

  // Actions
  /**
   * 表示月の選択時のハンドラー
   * @param yearMonth - 選択された年月
   */
  const onSelectedReceiveMonth = async (yearMonth: IYearMonth) => {
    state.receivedForecastYearMonthInfo = yearMonth;
    // Reset to first page when month changes
    state.tableContents.pagingOptions.offset = 0;
    // Reload data when month changes
    await fetchForecastAccuracyData();
    // Update table headers when received month changes
    state.tableContents.renderingKey += 1;
  };

  /**
   * ページング変更時のハンドラー
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.tableContents.pagingOptions = pagingOptions;
    // Fetch new data with updated pagination
    await fetchForecastAccuracyData();
  };

  /**
   * 表示月数の選択時のハンドラー
   * @param index - 選択されたインデックス
   */
  const onSelectedDisplayForecastsMonth = async (index: number) => {
    if (index >= 0 && index < DISPLAY_MONTHS_OPTIONS.length) {
      state.displayForecastsMonth = DISPLAY_MONTHS_OPTIONS[index];
      // Reset to first page when display months change
      state.tableContents.pagingOptions.offset = 0;
      // Regenerate data with new month count
      await fetchForecastAccuracyData();
      // Update table headers when month count changes
      state.tableContents.renderingKey += 1;
    }
  };

  /**
   * Update responsible user filter
   * @param userIds - ユーザーIDの配列
   */
  const updateResponsibleUserFilter = async (userIds: string[]) => {
    state.selectedResponsibleUserIdList = userIds;
    // Reset to first page when filter changes
    state.tableContents.pagingOptions.offset = 0;
    // Reload data with new filter
    await fetchForecastAccuracyData();
  };

  /**
   * Initialize the store
   * ストアを初期化
   */
  const init = async () => {
    await fetchForecastAccuracyData();
    // Update rendering key to ensure table re-renders when switching tabs
    state.tableContents.renderingKey += 1;
  };

  /**
   * Handle download action for a specific forecast
   * 特定の内示のダウンロードを処理
   * @param forecastForReceiptId - 受領用内示ID
   */
  const onClickDownload = (forecastForReceiptId: string) => {
    // Find the item data
    const item = tableData.value.find((data) => data.forecastForReceiptId === forecastForReceiptId);
    if (!item) {
      console.error('Download: Item not found for forecastForReceiptId:', forecastForReceiptId);
      toast.error(t('データが見つかりませんでした'));
      return;
    }

    // Calculate default start/end year month based on current display settings

    // Default start: displayForecastsMonth months before current receive month
    const defaultStartYearMonth = DateService.getShiftedIYearMonthByMonths(
      state.receivedForecastYearMonthInfo.year,
      state.receivedForecastYearMonthInfo.month,
      -state.displayForecastsMonth
    );

    // Default end: current receive month - 1 (previous month)
    const defaultEndYearMonth = DateService.getShiftedIYearMonthByMonths(
      state.receivedForecastYearMonthInfo.year,
      state.receivedForecastYearMonthInfo.month,
      -1
    );

    // Set modal state
    state.downloadModalState.targetForecastForReceiptId = forecastForReceiptId;
    state.downloadModalState.targetForecastName = item.forecastForReceiptName;
    state.downloadModalState.startYearMonth = defaultStartYearMonth;
    state.downloadModalState.endYearMonth = defaultEndYearMonth;
    state.downloadModalState.isOpen = true;
  };

  /**
   * Handle download apply button click from modal
   * モーダルのダウンロードボタンクリック時の処理
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  const onDownloadApply = async (startYearMonth: IYearMonth, endYearMonth: IYearMonth) => {
    const forecastForReceiptId = state.downloadModalState.targetForecastForReceiptId;

    // Validate date range
    const startDate = new Date(startYearMonth.year, startYearMonth.month - 1);
    const endDate = new Date(endYearMonth.year, endYearMonth.month - 1);

    if (startDate > endDate) {
      toast.error(t('開始月は終了月より前に設定してください'));
      return;
    }

    try {
      // Call UseCase to export forecast accuracy data
      const result = await diContainer.exportForecastAccuracyRateUseCase.Execute(
        forecastForReceiptId,
        startYearMonth.year,
        startYearMonth.month,
        endYearMonth.year,
        endYearMonth.month,
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        i18n.locale
      );

      if (result.status.type === 'error') {
        throw result.status.error;
      }

      // Get download URL and file name from response
      const { download_url, file_name } = result.status.data;

      // Trigger browser download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('ダウンロードを開始しました'));
      state.downloadModalState.isOpen = false;
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('ダウンロードに失敗しました'));
    }
  };

  /**
   * Close download modal
   * ダウンロードモーダルを閉じる
   */
  const closeDownloadModal = () => {
    state.downloadModalState.isOpen = false;
  };

  /**
   * UIヘッダーキーをバックエンドのカラム名に変換するヘルパー関数
   * @param uiKey - UIヘッダーキー
   * @returns バックエンドのカラム名、または変換不要な場合は元のキー
   */
  const convertUiKeyToBackendKey = (uiKey?: string): string | undefined => {
    if (uiKey?.includes('customerAnnounceForAccuracy')) {
      // 月別正答率カラムは accuracyRate として処理
      return 'accuracyRate';
    } else if (uiKey === 'ForecastAccuracyName') {
      return 'ForecastForReceiptName';
    } else if (uiKey === 'ForecastAccuracyResponsibleUser') {
      return 'ForecastForReceiptResponsibleUser';
    } else if (uiKey === 'ForecastAccuracyPreviousMonthSalesResult') {
      return 'previousMonthSalesResult';
    } else if (uiKey === 'ForecastAccuracyPreviousMonthForecast') {
      return 'previousMonthForecast';
    } else if (uiKey === 'ForecastAccuracyPreviousMonthAccuracyRate') {
      // 前月正答率も accuracyRate として処理（前月が対象月）
      return 'accuracyRate';
    } else {
      return uiKey;
    }
  };

  /**
   * UIヘッダーキーからソート対象の年月を取得
   * @param uiKey - UIヘッダーキー
   * @returns ソート対象の年月、または undefined
   */
  const getSortTargetYearMonth = (uiKey?: string): IYearMonth | undefined => {
    if (!uiKey) return undefined;

    if (uiKey === 'ForecastAccuracyPreviousMonthAccuracyRate') {
      // 前月正答率の場合は、受領月の1ヶ月前
      return DateService.getShiftedIYearMonthByMonths(
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        -1
      );
    }

    // customerAnnounceForAccuracy-N の形式の場合
    const match = uiKey.match(/customerAnnounceForAccuracy-(\d+)/);
    if (match) {
      const columnIndex = parseInt(match[1], 10);
      // カラムインデックス1は最も古い月（displayForecastsMonth + 1 ヶ月前）
      // カラムインデックスNは（displayForecastsMonth + 1 - N + 1 = displayForecastsMonth - N + 2）ヶ月前
      const monthsBack = state.displayForecastsMonth - columnIndex + 2;
      return DateService.getShiftedIYearMonthByMonths(
        state.receivedForecastYearMonthInfo.year,
        state.receivedForecastYearMonthInfo.month,
        -monthsBack
      );
    }

    return undefined;
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   */
  const sortTableContentItems = async (sortKey: TableContentProperty) => {
    if (sortKey.key === state.tableContents.sortKey) {
      state.tableContents.isAsc = !state.tableContents.isAsc;
    } else {
      state.tableContents.isAsc = false;
    }
    state.tableContents.sortKey = sortKey.key;

    // ソート対象の年月を設定（月別正答率カラムの場合）
    state.tableContents.sortTargetYearMonth = getSortTargetYearMonth(sortKey.key);

    // ソート設定の永続化対応
    await saveSort(FORECAST_ACCURACY_TABLE_TYPE, state.tableContents.sortKey, state.tableContents.isAsc);

    // UIのソート表示を更新
    const updatedHeaders = getTableHeaders(state.displayForecastsMonth);
    updatedHeaders.contentProperties = updatedHeaders.contentProperties.map((property) => {
      const currentIsAsc = state.tableContents.isAsc ?? true;
      property.sortOption.isAsc = property.key === state.tableContents.sortKey ? currentIsAsc : true;
      return property;
    });

    // Reset to first page when sort changes
    state.tableContents.pagingOptions.offset = 0;

    // Fetch data with new sort settings
    await fetchForecastAccuracyData();

    // Force re-render table
    state.tableContents.renderingKey += 1;
  };

  return {
    // State
    state,

    // Computed
    tableContentItems,
    monthsOptionsDisplayForecast,
    tableHeaders,

    // Actions
    init,
    onSelectedReceiveMonth,
    onChangePaging,
    onSelectedDisplayForecastsMonth,
    updateResponsibleUserFilter,
    onClickDownload,
    onDownloadApply,
    closeDownloadModal,
    sortTableContentItems,
  };
});
